import React, { useState, useEffect, useMemo, useCallback } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import { getImageURL } from '../../utils/api';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fmtTime(t) {
  if (!t) return '';
  const [h, m] = String(t).split(':');
  const hr = parseInt(h, 10);
  const ampm = hr >= 12 ? 'PM' : 'AM';
  return `${hr % 12 || 12}:${m} ${ampm}`;
}

function fmtDate(d) {
  if (!d) return '';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
}

// ─── WhatsApp message formatter ───────────────────────────────────────────────

function buildWhatsAppMessage(courseName, batchLabel, students, date) {
  const lines = [];
  lines.push(`⚠️ *Partial Attendance Warning*`);
  lines.push(`*Date:* ${fmtDate(date)}`);
  lines.push(`*Course:* ${courseName}`);
  lines.push('');
  lines.push('Dear Students,');
  lines.push('');
  lines.push(
    'Please be informed that the following students attended class *partially* today. ' +
    'Partial attendance is *strictly not acceptable* and falls below the required standards.'
  );
  lines.push('');

  students.forEach((s, i) => {
    lines.push(`*${i + 1}. ${s.studentName}* (${s.registrationNumber})`);
    s.sessions.forEach((sess) => {
      const time = sess.startTime ? ` — ${fmtTime(sess.startTime)}` : '';
      const subject = `*${sess.subjectShortName || sess.subjectName}*${time}`;
      if (sess.status === 'Present') {
        lines.push(`   ✅ Present: ${subject}`);
      } else if (sess.attendanceMarked === false) {
        lines.push(`   ⚠️ Not marked: ${subject}`);
      } else {
        lines.push(`   ❌ Absent: ${subject}`);
      }
    });
  });

  lines.push('');
  lines.push(
    '*All students are required to attend every scheduled session in full.* ' +
    'Leaving early, arriving late, or skipping any session is *not acceptable*. ' +
    'Continued partial attendance may result in *disciplinary action*.'
  );
  lines.push('');
  lines.push('Please ensure your *full presence* in all future classes.');
  lines.push('');
  lines.push('Thank you.');

  return lines.join('\n');
}

// Reminder for the lecturers' group -- lists every class today that still
// has no attendance record at all for any of the students loaded on this
// page, grouped by subject/time so a lecturer isn't pinged once per student.
function buildAttendanceReminderMessage(unmarkedSessions, date) {
  const lines = [];
  lines.push('📋 *Attendance Marking Reminder*');
  lines.push(`*Date:* ${fmtDate(date)}`);
  lines.push('');
  lines.push('The following classes do not have attendance marked yet:');
  lines.push('');

  unmarkedSessions.forEach((s, i) => {
    const time = s.startTime ? ` — ${fmtTime(s.startTime)}` : '';
    const lecturer = s.staffName ? ` (Lecturer: ${s.staffName})` : '';
    lines.push(`${i + 1}. *${s.subjectShortName || s.subjectName}*${time}${lecturer}`);
  });

  lines.push('');
  lines.push('Kindly mark attendance for the above class(es) as soon as possible. Thank you!');

  return lines.join('\n');
}

// ─── Avatar ───────────────────────────────────────────────────────────────────

function Avatar({ name, imagePath }) {
  const initials = (name || '?').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  if (imagePath) {
    return (
      <img
        src={getImageURL(imagePath)}
        alt={name}
        className="w-8 h-8 rounded-full object-cover flex-shrink-0"
        onError={(e) => { e.target.style.display = 'none'; }}
      />
    );
  }
  return (
    <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
      {initials}
    </div>
  );
}

// ─── Copy button ──────────────────────────────────────────────────────────────

function CopyButton({ text, label = 'Copy for WhatsApp', title = 'Copy WhatsApp warning message', className = '' }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  }, [text]);

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={title}
      className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-lg border transition-all duration-150 ${
        copied
          ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
          : 'bg-white border-gray-200 text-gray-500 hover:border-indigo-300 hover:text-indigo-600 hover:bg-indigo-50'
      } ${className}`}
    >
      {copied ? (
        <>
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          Copied!
        </>
      ) : (
        <>
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v2m-6 12h8a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-8a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2z" />
          </svg>
          {label}
        </>
      )}
    </button>
  );
}

// ─── Student row ──────────────────────────────────────────────────────────────

function StudentRow({ student }) {
  const unmarkedCount = student.sessions.filter((s) => s.attendanceMarked === false).length;
  return (
    <div className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50/60 border-b border-gray-100 last:border-0">
      <Avatar name={student.studentName} imagePath={student.imagePath} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900">{student.studentName}</p>
        <p className="text-xs text-gray-400 mb-1.5">{student.registrationNumber}</p>
        <div className="flex flex-wrap gap-1.5">
          {student.sessions.map((s) => {
            const isPresent = s.status === 'Present';
            // Attendance never taken for this class -- shown as "not marked"
            // rather than "absent" so it isn't mistaken for a confirmed absence.
            const isUnmarked = !isPresent && s.attendanceMarked === false;
            const pillClass = isPresent
              ? 'bg-emerald-100 text-emerald-700'
              : isUnmarked
              ? 'border border-dashed border-gray-300 bg-gray-100 text-gray-500'
              : 'bg-red-100 text-red-700';
            return (
              <span
                key={s.scheduleId}
                title={isUnmarked ? 'Attendance was not marked for this class' : undefined}
                className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full ${pillClass}`}
              >
                {isPresent ? '✓' : isUnmarked ? '?' : '✗'}{' '}
                {s.subjectShortName || s.subjectName}
                {s.startTime ? ` ${fmtTime(s.startTime)}` : ''}
                {isUnmarked ? ' (not marked)' : ''}
              </span>
            );
          })}
        </div>
      </div>
      <div className="text-right shrink-0 mt-1">
        <span className="text-xs font-bold text-emerald-600">{student.presentCount}P</span>
        <span className="mx-1 text-gray-300">/</span>
        <span className="text-xs font-bold text-red-500">{student.absentCount}A</span>
        {unmarkedCount > 0 && (
          <div
            className="mt-0.5 text-[10px] font-medium text-gray-400"
            title="Of the above, this many classes never had attendance taken"
          >
            {unmarkedCount} not marked
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Batch group (WhatsApp group sub-section) ─────────────────────────────────

function BatchGroup({ courseName, batchLabel, students, date }) {
  const message = useMemo(
    () => buildWhatsAppMessage(courseName, batchLabel, students, date),
    [courseName, batchLabel, students, date]
  );

  return (
    <div className="border-t border-gray-100 first:border-t-0">
      {/* Batch header */}
      <div className="flex items-center justify-between gap-3 px-4 py-2 bg-gray-50 border-b border-gray-100">
        <div className="flex items-center gap-2 min-w-0">
          <svg className="w-3.5 h-3.5 text-green-500 shrink-0" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
            <path d="M12 0C5.373 0 0 5.373 0 12c0 2.125.558 4.122 1.532 5.857L.057 23.882l6.198-1.625A11.937 11.937 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.894a9.887 9.887 0 0 1-5.031-1.372l-.361-.214-3.741.981.998-3.648-.235-.374A9.859 9.859 0 0 1 2.106 12C2.106 6.527 6.527 2.106 12 2.106S21.894 6.527 21.894 12 17.473 21.894 12 21.894z"/>
          </svg>
          <span className="text-xs font-semibold text-gray-700 truncate">
            {batchLabel || 'Unassigned Group'}
          </span>
        </div>
        <CopyButton text={message} />
      </div>

      {/* Students */}
      {students.map((s) => (
        <StudentRow key={`${s.studentId}-${s.registrationNumber}`} student={s} />
      ))}
    </div>
  );
}

// ─── Course group card ────────────────────────────────────────────────────────

function CourseGroup({ courseName, batches, date }) {
  const total = batches.reduce((n, b) => n + b.students.length, 0);
  return (
    <div className="rounded-xl border border-gray-200 overflow-hidden bg-white shadow-sm">
      {/* Course header */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-amber-50 border-b border-amber-200">
        <span className="text-sm font-semibold text-amber-800 truncate">{courseName}</span>
        <span className="shrink-0 ml-2 text-xs font-bold text-white bg-amber-500 rounded-full px-2 py-0.5">
          {total} student{total !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Batch sub-groups */}
      {batches.map(({ batchLabel, students }) => (
        <BatchGroup
          key={batchLabel}
          courseName={courseName}
          batchLabel={batchLabel}
          students={students}
          date={date}
        />
      ))}
    </div>
  );
}

// ─── Spinner ──────────────────────────────────────────────────────────────────

function Spinner() {
  return (
    <div className="flex justify-center py-16">
      <svg className="animate-spin w-6 h-6 text-indigo-500" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
      </svg>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function PartialAttendanceReport() {
  const [date, setDate]       = useState(todayStr());
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [search, setSearch]   = useState('');

  useEffect(() => {
    setLoading(true);
    setError(null);
    setSearch('');
    axiosInstance.get('/admin/coordinator-reports/partial-attendance', { params: { date } })
      .then((r) => setData(r.data))
      .catch(() => setError('Failed to load attendance data.'))
      .finally(() => setLoading(false));
  }, [date]);

  // Filter then group: course → batch
  const grouped = useMemo(() => {
    if (!data?.students) return [];
    const q = search.trim().toLowerCase();
    const list = q
      ? data.students.filter(
          (s) =>
            s.studentName.toLowerCase().includes(q) ||
            (s.registrationNumber || '').toLowerCase().includes(q)
        )
      : data.students;

    // Course map
    const courseMap = new Map();
    list.forEach((s) => {
      const cKey = s.courseName || 'Unknown Course';
      if (!courseMap.has(cKey)) courseMap.set(cKey, new Map());
      const batchMap = courseMap.get(cKey);
      const bKey = s.batchLabel || 'Unassigned Group';
      if (!batchMap.has(bKey)) batchMap.set(bKey, []);
      batchMap.get(bKey).push(s);
    });

    return Array.from(courseMap.entries()).map(([courseName, batchMap]) => ({
      courseName,
      batches: Array.from(batchMap.entries()).map(([batchLabel, students]) => ({ batchLabel, students })),
    }));
  }, [data, search]);

  const totalStudents = grouped.reduce((n, g) => n + g.batches.reduce((m, b) => m + b.students.length, 0), 0);

  // Every distinct class (subject + time) across all loaded students that
  // still has no attendance record at all -- deduped so a lecturer teaching
  // several of these partial-attendance students isn't listed more than once.
  const unmarkedSessions = useMemo(() => {
    if (!data?.students) return [];
    const bySchedule = new Map();
    data.students.forEach((s) => {
      s.sessions.forEach((sess) => {
        if (sess.attendanceMarked === false && !bySchedule.has(sess.scheduleId)) {
          bySchedule.set(sess.scheduleId, sess);
        }
      });
    });
    return Array.from(bySchedule.values()).sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
  }, [data]);

  const attendanceReminderMessage = useMemo(
    () => buildAttendanceReminderMessage(unmarkedSessions, date),
    [unmarkedSessions, date]
  );

  return (
    <DashboardLayout title="Partial Attendance Report">
      <div className="min-h-screen bg-gray-50 px-6 py-8 space-y-6">

        {/* Header */}
        <div className="border-b border-gray-200 pb-5">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 tracking-tight">Partial Attendance</h1>
              <p className="text-xs text-gray-400 mt-0.5">{fmtDate(date)}</p>
            </div>
          </div>
        </div>

        {/* Filter bar */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3 flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-[11px] font-medium text-gray-500 mb-1">Date</label>
            <input
              type="date"
              value={date}
              max={todayStr()}
              onChange={(e) => setDate(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {!loading && unmarkedSessions.length > 0 && (
            <div>
              <label className="block text-[11px] font-medium text-gray-500 mb-1">
                {unmarkedSessions.length} class{unmarkedSessions.length !== 1 ? 'es' : ''} not marked
              </label>
              <CopyButton
                text={attendanceReminderMessage}
                label="Copy Attendance Reminder"
                title="Copy a WhatsApp message reminding lecturers to mark attendance"
              />
            </div>
          )}

          {!loading && data?.students?.length > 0 && (
            <div className="ml-auto">
              <label className="block text-[11px] font-medium text-gray-500 mb-1">Search</label>
              <input
                type="text"
                placeholder="Name or reg. no…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 w-48"
              />
            </div>
          )}
        </div>

        {/* States */}
        {loading && <Spinner />}
        {!loading && error && (
          <div className="text-center py-12 text-red-500 text-sm">{error}</div>
        )}

        {!loading && !error && data && (
          <>
            <div className="text-sm text-gray-500">
              <span className="font-semibold text-amber-600">{totalStudents}</span>
              {' '}student{totalStudents !== 1 ? 's' : ''} with partial attendance
              {search ? ' matching search' : ''}
            </div>

            {grouped.length === 0 && (
              <div className="text-center py-12 text-gray-400 text-sm italic">
                {search
                  ? 'No students match the search.'
                  : 'No students with partial attendance on this date.'}
              </div>
            )}

            <div className="space-y-4">
              {grouped.map(({ courseName, batches }) => (
                <CourseGroup
                  key={courseName}
                  courseName={courseName}
                  batches={batches}
                  date={date}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
